import { ThumbsUp, ThumbsDown, AlertTriangle, MessageCircle, ExternalLink, RotateCcw, Archive } from 'lucide-react';
import { FunnelStageBadge } from './FunnelStageBadge';
import { useQuickRate, useSubmitQuickRate } from '../hooks/useApi';
import { useToast } from '../contexts/ToastContext';
import type { QuickRating } from '../types';

interface ApplicationActionBarProps {
  applicationId: number;
  srApplicationId: string | null;
  srStage: string | null;
  overallScore: number | null;
  summary: string | null;
  onChat: () => void;
  onRerun: () => void;
  onArchive: () => void;
}

export function ApplicationActionBar({
  applicationId,
  srApplicationId,
  srStage,
  overallScore,
  summary,
  onChat,
  onRerun,
  onArchive,
}: ApplicationActionBarProps) {
  const { data: ratingData } = useQuickRate(applicationId);
  const submitMutation = useSubmitQuickRate();
  const { showToast } = useToast();

  const currentRating = ratingData?.rating ?? null;

  const scoreDisplay = overallScore !== null
    ? (overallScore / 10).toFixed(1)
    : null;

  const scoreColor = overallScore !== null
    ? overallScore * 2 >= 80 ? 'text-emerald-600 dark:text-emerald-400'
      : overallScore * 2 >= 60 ? 'text-blue-600 dark:text-blue-400'
      : overallScore * 2 >= 40 ? 'text-amber-600 dark:text-amber-400'
      : 'text-red-600 dark:text-red-400'
    : '';

  const handleRate = async (rating: QuickRating) => {
    try {
      await submitMutation.mutateAsync({ applicationId, rating });
      showToast('Rating saved', 'success');
    } catch {
      showToast('Failed to save rating', 'error');
    }
  };

  const rateButtonClass = (rating: QuickRating) => {
    const active = currentRating === rating;
    const base = 'flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1';
    if (rating === 'accurate') return `${base} ${active ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 ring-2 ring-emerald-500' : 'text-gray-600 dark:text-gray-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:text-emerald-700 dark:hover:text-emerald-400'}`;
    if (rating === 'inaccurate') return `${base} ${active ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 ring-2 ring-red-500' : 'text-gray-600 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-400'}`;
    return `${base} ${active ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 ring-2 ring-amber-500' : 'text-gray-600 dark:text-gray-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:text-amber-700 dark:hover:text-amber-400'}`;
  };

  const iconBtnClass = 'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none';

  return (
    <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="px-4 sm:px-6 py-2 flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">

        {/* Stage badge */}
        {srStage && (
          <FunnelStageBadge stage={srStage} className="shrink-0" />
        )}

        {/* AI score */}
        {scoreDisplay !== null && (
          <div className="flex items-baseline gap-0.5 shrink-0">
            <span className={`text-lg font-bold tabular-nums ${scoreColor}`}>{scoreDisplay}</span>
            <span className="text-xs text-gray-400 dark:text-gray-500">/5</span>
          </div>
        )}

        {/* Arbie summary — truncated, fills remaining space */}
        <p className="text-xs text-gray-500 dark:text-gray-400 min-w-0 flex-1 truncate">
          {summary
            ? summary.length > 120 ? `${summary.slice(0, 120)}…` : summary
            : 'No AI summary yet'
          }
        </p>

        {/* Quick-rate cluster */}
        <div className="flex items-center gap-0.5 shrink-0 border-l border-gray-200 dark:border-gray-700 pl-2 sm:pl-3">
          <button
            data-testid="quick-rate-accurate"
            onClick={() => handleRate('accurate')}
            className={rateButtonClass('accurate')}
            title="Accurate — AI review was correct"
            disabled={submitMutation.isPending}
          >
            <ThumbsUp className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Accurate</span>
          </button>
          <button
            data-testid="quick-rate-inaccurate"
            onClick={() => handleRate('inaccurate')}
            className={rateButtonClass('inaccurate')}
            title="Inaccurate — AI review was wrong"
            disabled={submitMutation.isPending}
          >
            <ThumbsDown className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Inaccurate</span>
          </button>
          <button
            data-testid="quick-rate-invalid"
            onClick={() => handleRate('invalid')}
            className={rateButtonClass('invalid')}
            title="Invalid — candidate or role was not a real match"
            disabled={submitMutation.isPending}
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Invalid</span>
          </button>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-0.5 shrink-0 border-l border-gray-200 dark:border-gray-700 pl-2 sm:pl-3">
          <button
            data-testid="action-bar-chat"
            onClick={onChat}
            className={iconBtnClass}
            title="Chat with Arbie"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Chat</span>
          </button>

          {srApplicationId && (
            <a
              data-testid="action-bar-open-sr"
              href={`https://www.smartrecruiters.com/app/people/applications/${srApplicationId}/`}
              target="_blank"
              rel="noopener noreferrer"
              className={iconBtnClass}
              title="Open in SmartRecruiters"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Open in SR</span>
            </a>
          )}

          <button
            data-testid="action-bar-rerun"
            onClick={onRerun}
            className={iconBtnClass}
            title="Re-run AI review"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Re-run</span>
          </button>

          <button
            data-testid="action-bar-archive"
            onClick={onArchive}
            className={`${iconBtnClass} hover:text-red-600 dark:hover:text-red-400`}
            title="Archive application"
          >
            <Archive className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Archive</span>
          </button>
        </div>
      </div>
    </div>
  );
}
